import React, { useEffect, useState } from 'react';
import { Loader2, Banknote } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { oapi, fmtMoney, minorToInput, newKey, todayYmd } from '@/lib/officialsApi';
import { Field, METHOD_LABEL } from './PayBits';

export const blankPayment = () => ({ amount: '', paid_on: todayYmd(), method: 'bank_transfer', reference: '', notes: '', notify: true });
const toMinor = (v) => Math.round(Number(v || 0) * 100);

// Date / method / reference / notes / notify fields shared by single and batch payments.
export const MethodFields = ({ form, set, testId }) => (
  <>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Payment date"><Input type="date" value={form.paid_on} onChange={e => set('paid_on', e.target.value)} data-testid={`${testId}-date`} /></Field>
      <Field label="Method">
        <Select value={form.method} onValueChange={v => set('method', v)}>
          <SelectTrigger data-testid={`${testId}-method`}><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(METHOD_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
    </div>
    <Field label="Reference (optional)"><Input value={form.reference} onChange={e => set('reference', e.target.value)} placeholder="Transfer ID, cheque number…" data-testid={`${testId}-reference`} /></Field>
    <Field label="Notes (optional)"><Textarea rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} data-testid={`${testId}-notes`} /></Field>
    <label className="flex items-center gap-2 text-sm text-slate-700"><Switch checked={form.notify} onCheckedChange={v => set('notify', v)} data-testid={`${testId}-notify`} />Email the official a payment confirmation</label>
  </>
);

const DueBox = ({ due, currency, amount }) => {
  if (!due) return <div className="py-3 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  if (due.minor <= 0) return <p className="text-sm rounded-xl bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2" data-testid="record-payment-nothing-due">Nothing approved is waiting to be paid. Approve earnings first.</p>;
  const left = due.minor - toMinor(amount);
  return (
    <div className="rounded-xl bg-slate-50 border px-3 py-2 text-sm" data-testid="record-payment-due">
      <div className="flex justify-between"><span className="text-slate-600">Outstanding ({due.items} item{due.items === 1 ? '' : 's'})</span><b className="tabular-nums">{fmtMoney(due.minor, currency)}</b></div>
      {toMinor(amount) > 0 && left > 0 && <div className="text-xs text-amber-700 mt-1" data-testid="record-payment-partial">Partial payment · {fmtMoney(left, currency)} will stay outstanding</div>}
      {left < 0 && <div className="text-xs text-red-600 mt-1" data-testid="record-payment-over">That's more than what is owed</div>}
    </div>
  );
};

export const RecordPaymentDialog = ({ target, orgId, currency, officials, onClose, onDone }) => {
  const [oid, setOid] = useState('');
  const [due, setDue] = useState(null);
  const [form, setForm] = useState(blankPayment());
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (target) { setOid(target.official_id || ''); setForm(blankPayment()); setKey(newKey()); } }, [target]);
  useEffect(() => {
    setDue(null);
    if (!target || !oid) return;
    oapi(`/officials/payments/officials/${oid}`, { orgId }).then(d => {
      const ids = target.line_ids;
      const owed = d.lines.filter(l => l.remaining_minor !== 0 && (!ids || ids.includes(l.id)));
      const sum = owed.reduce((s, l) => s + l.remaining_minor, 0);
      setDue({ minor: sum, items: owed.length });
      setForm(f => ({ ...f, amount: sum > 0 ? minorToInput(sum) : '' }));
    }).catch(e => toast.error(e.message));
  }, [target, oid, orgId]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = async () => {
    setBusy(true);
    try {
      const r = await oapi('/officials/payments/payments', { method: 'POST', orgId, body: { ...form, official_id: oid, line_ids: target.line_ids, idempotency_key: key } });
      toast.success(r.payment.duplicate ? 'This payment was already recorded' : `Payment of ${fmtMoney(r.payment.amount_minor, currency)} recorded`);
      onDone();
      onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const name = target?.official_name || officials.find(o => o.id === oid)?.full_name;
  const valid = oid && due && due.minor > 0 && toMinor(form.amount) > 0 && toMinor(form.amount) <= due.minor && form.paid_on;
  return (
    <Dialog open={!!target} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="record-payment-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#00247D]">Record payment</DialogTitle>
          <DialogDescription>
            {name ? `Paying ${name}` : 'Choose who you paid'}{target?.line_ids ? ` · ${target.line_ids.length} selected item${target.line_ids.length === 1 ? '' : 's'}` : ''}. Applied to the oldest approved items first.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {!target?.official_id && (
            <Field label="Official">
              <Select value={oid} onValueChange={setOid}>
                <SelectTrigger data-testid="record-payment-official"><SelectValue placeholder="Choose an official" /></SelectTrigger>
                <SelectContent>{officials.map(o => <SelectItem key={o.id} value={o.id}>{o.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          )}
          {oid && <DueBox due={due} currency={currency} amount={form.amount} />}
          <Field label={`Amount paid (${currency})`}>
            <Input type="number" min="0" step="0.01" value={form.amount} onChange={e => set('amount', e.target.value)} data-testid="record-payment-amount" />
          </Field>
          <MethodFields form={form} set={set} testId="record-payment" />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={busy || !valid} onClick={submit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="record-payment-submit">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Banknote className="w-4 h-4 mr-1.5" />}Record payment
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
